import { Outlet, Link, useLocation } from "react-router-dom";
import { FileImage, Images, Scissors, Combine, Clock, Shield, Minimize2, RotateCw, Trash, ArrowUpDown, Menu, X, ScanLine, PenTool, Edit3, FileText, Droplets, Lock, Unlock } from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const Layout = () => {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { label: "Convert", items: [
      { path: "/pdf-to-image", icon: FileImage, label: "PDF to Image" },
      { path: "/image-to-pdf", icon: Images, label: "Image to PDF" },
      { path: "/pdf-ocr", icon: ScanLine, label: "PDF OCR" },
    ]},
    { label: "Organize", items: [
      { path: "/pdf-merge", icon: Combine, label: "Merge PDF" },
      { path: "/pdf-split", icon: Scissors, label: "Split PDF" },
      { path: "/pdf-organize", icon: ArrowUpDown, label: "Organize PDF" },
    ]},
    { label: "Edit", items: [
      { path: "/pdf-edit", icon: Edit3, label: "Edit PDF" },
      { path: "/pdf-sign", icon: PenTool, label: "Sign PDF" },
      { path: "/pdf-rotate", icon: RotateCw, label: "Rotate PDF" },
      { path: "/pdf-delete-pages", icon: Trash, label: "Delete Pages" },
      { path: "/pdf-watermark", icon: Droplets, label: "Watermark PDF" },
    ]},
    { label: "Security", items: [
      { path: "/pdf-protect", icon: Lock, label: "Protect PDF" },
      { path: "/pdf-unlock", icon: Unlock, label: "Unlock PDF" },
    ]},
    { label: "Optimize", items: [
      { path: "/pdf-compress", icon: Minimize2, label: "Compress PDF" },
      { path: "/pdf-scanned", icon: FileText, label: "Scanned Effect" },
    ]},
  ];

  return (
    <div className="min-h-screen flex flex-col relative z-10">
      {/* Header */}
      <header className="header-container" data-testid="header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link 
              to="/" 
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              data-testid="logo-link"
            >
              <div className="w-10 h-10 bg-black flex items-center justify-center">
                <span className="text-white font-bold text-lg font-['Outfit']">SF</span>
              </div>
              <span className="text-xl font-semibold tracking-tight font-['Outfit']">
                SwissFlow PDF
              </span>
            </Link>

            {!isHome && (
              <>
                {/* Desktop Navigation */}
                <nav className="hidden md:flex items-center gap-2" data-testid="nav-menu">
                  {navItems.map((group) => (
                    <DropdownMenu key={group.label}>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-sm font-medium">
                          {group.label}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-48">
                        {group.items.map((item) => (
                          <DropdownMenuItem key={item.path} asChild>
                            <Link 
                              to={item.path} 
                              className={`flex items-center gap-2 ${
                                location.pathname === item.path ? "text-[#FF3B30]" : ""
                              }`}
                            >
                              <item.icon className="w-4 h-4" />
                              {item.label}
                            </Link>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ))}
                </nav>

                {/* Mobile Menu Button */}
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="md:hidden p-2 hover:bg-secondary"
                  data-testid="mobile-menu-button"
                >
                  {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
              </>
            )}
          </div>

          {/* Mobile Navigation */}
          {!isHome && mobileMenuOpen && (
            <div className="md:hidden border-t border-border py-4" data-testid="mobile-nav">
              {navItems.map((group) => (
                <div key={group.label} className="mb-4">
                  <p className="text-xs font-mono uppercase text-muted-foreground mb-2 px-2">
                    {group.label}
                  </p>
                  {group.items.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-2 px-2 py-2 hover:bg-secondary ${
                        location.pathname === item.path ? "text-[#FF3B30]" : ""
                      }`}
                    >
                      <item.icon className="w-4 h-4" />
                      <span className="text-sm">{item.label}</span>
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="w-4 h-4 text-[#FF3B30]" />
                <span>Files auto-delete after 30 minutes</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Shield className="w-4 h-4 text-[#FF3B30]" />
                <span>Your privacy is protected</span>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/privacy-policy" className="text-sm text-muted-foreground hover:text-[#FF3B30] transition-colors" data-testid="privacy-link">
                Privacy Policy
              </Link>
              <Link to="/contact" className="text-sm text-muted-foreground hover:text-[#FF3B30] transition-colors" data-testid="contact-link">
                Contact Us
              </Link>
              <p className="text-sm text-muted-foreground font-mono">
                SwissFlow PDF © {new Date().getFullYear()}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
